import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { PopulationData } from '../../demographicsService'
import {
  getPopulationPyramidBarLayout,
  getPopulationPyramidSeriesData,
} from './PopulationPyramidChart.utils'

interface PopulationPyramidChartProps {
  data: PopulationData
  height?: number
  t: TFunction
}

export interface PopulationPyramidChartRef {
  exportChart: () => string | null
}

export const PopulationPyramidChart = forwardRef<
  PopulationPyramidChartRef,
  PopulationPyramidChartProps
>(({ data, height = 400, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    const { ageGroups, maleData, femaleData } =
      getPopulationPyramidSeriesData(data)
    const { barGap, barWidth } = getPopulationPyramidBarLayout()

    // Convert male data to negative values for left side
    const maleNegativeData = maleData.map((value) => -value)

    // Find max value for scaling
    const maxValue = Math.max(...maleData, ...femaleData)
    const maxRange = Math.ceil(maxValue * 1.1)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params: any) {
          const male = Math.abs(params[0].value)
          const female = params[1].value
          const ageGroup = params[0].axisValue
          const total = male + female
          const malePercent =
            total > 0 ? ((male / total) * 100).toFixed(1) : '0'
          const femalePercent =
            total > 0 ? ((female / total) * 100).toFixed(1) : '0'

          return `
            <div style="text-align: left;">
              <strong>${t('charts.populationPyramid.tooltips.ageGroup')}: ${ageGroup}</strong><br/>
              <span style="color: #3b82f6;">● ${t('charts.populationPyramid.legend.male')}: ${male} (${malePercent}%)</span><br/>
              <span style="color: #ec4899;">● ${t('charts.populationPyramid.legend.female')}: ${female} (${femalePercent}%)</span><br/>
              <strong>${t('charts.populationPyramid.tooltips.total')}: ${total}</strong>
            </div>
          `
        },
      },
      legend: {
        data: [
          t('charts.populationPyramid.legend.male'),
          t('charts.populationPyramid.legend.female'),
        ],
        top: 10,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: '15%',
        right: '15%',
        top: 50,
        bottom: 30,
        containLabel: false,
      },
      xAxis: [
        {
          type: 'value',
          min: -maxRange,
          max: maxRange,
          axisLabel: {
            formatter: function (value: number) {
              return Math.abs(value).toString()
            },
            fontSize: 11,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed',
            },
          },
        },
      ],
      yAxis: [
        {
          type: 'category',
          data: ageGroups,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontSize: 11,
            margin: 8,
            color: '#1f2937',
          },
        },
      ],
      series: [
        {
          name: t('charts.populationPyramid.legend.male'),
          type: 'bar',
          data: maleNegativeData,
          itemStyle: {
            color: '#3b82f6',
          },
          barWidth,
          barGap,
          emphasis: {
            itemStyle: {
              color: '#2563eb',
            },
          },
        },
        {
          name: t('charts.populationPyramid.legend.female'),
          type: 'bar',
          data: femaleData,
          itemStyle: {
            color: '#ec4899',
          },
          barWidth,
          barGap,
          emphasis: {
            itemStyle: {
              color: '#db2777',
            },
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Add real estate insights overlay
    const totalPopulation = data.total_population
    const youngProfessionals = data.total_population_20_39
    const families =
      (data.total_population_20_39 + data.total_population_40_64) * 0.6 // Estimate families
    const seniors = data.total_population_65_79 + data.total_population_80_plus

    // Add text annotations for key demographics
    const annotations = []

    if (youngProfessionals / totalPopulation > 0.3) {
      annotations.push({
        type: 'text',
        x: '70%',
        y: '75%',
        style: {
          text: t('charts.populationPyramid.insights.rentalMarket'),
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#3b82f6',
          textAlign: 'center',
        },
      })
    }

    if (families / totalPopulation > 0.4) {
      annotations.push({
        type: 'text',
        x: '70%',
        y: '50%',
        style: {
          text: t('charts.populationPyramid.insights.familyHousing'),
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#059669',
          textAlign: 'center',
        },
      })
    }

    if (seniors / totalPopulation > 0.25) {
      annotations.push({
        type: 'text',
        x: '70%',
        y: '25%',
        style: {
          text: t('charts.populationPyramid.insights.accessibility'),
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#7c3aed',
          textAlign: 'center',
        },
      })
    }

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '300px',
      }}
    />
  )
})

PopulationPyramidChart.displayName = 'PopulationPyramidChart'
